export type UserRole = 'system_admin' | 'city_admin' | 'member' | 'read_only';

export interface SoftDeleteFields {
  deleted_at: string | null;
  deleted_by_user_id: number | null;
  deleted_reason: string | null;
}

export type EntityType =
  | 'inserat'
  | 'adresse'
  | 'haus'
  | 'einheit'
  | 'vermieter'
  | 'verfahren'
  | 'anfrage'
  | 'bebauungsplan';

export interface LinkedCounts {
  inserate?: number;
  verfahren?: number;
  einheiten?: number;
  anfragen?: number;
  haeuser?: number;
}

export interface BulkDeleteResponseBody {
  deleted: number;
  skipped: number[];
  blocked: Record<number, LinkedCounts>;
}

export interface RestoreResponseBody {
  restored: number;
  skipped: number[];
}

export interface MapCenter {
  latitude: number;
  longitude: number;
  plz: string;
}

export interface City {
  id: number;
  name: string;
  slug: string;
  timezone: string;
  is_active: boolean;
  logo_url: string | null;
  default_map_center: MapCenter | null;
}

export interface User {
  id: number;
  email: string;
  full_name: string;
  role: UserRole;
  city_id: number | null;
  city: City | null;
  is_active: boolean;
  created_at: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthToken {
  access_token: string;
  token_type: string;
}

export interface Search {
  id: number;
  city_id: number;
  created_by: number | null;
  location: string;
  location_type: string | null;
  max_listings: number;
  total_listings_found: number;
  total_dom_cards: number;
  total_before_filter: number;
  filter_summary: string | null;
  search_status: 'pending' | 'in_progress' | 'completed' | 'failed';
  error_message: string | null;
  detail_scrape_status: string | null;
  detail_scrape_progress: number;
  detail_scrape_total: number;
  detail_scrape_started_at: string | null;
  detail_scrape_completed_at: string | null;
  created_at: string;
  completed_at: string | null;
}

export type PeriodCategory =
  | 'frei_buchbar'
  | 'frei_nicht_buchbar'
  | 'nicht_verfuegbar'
  | 'booking'
  | 'blocker'
  | 'unbekannt';

export type PeriodSource = 'auto' | 'transition' | 'override';

export type OverrideCategory = 'booking' | 'blocker';

/** Auswahlwerte für den Sachbearbeiter-Picker: Override-Kategorien plus
 *  „zurück auf Auto" (nicht_verfuegbar). */
export type OverridePickerValue = OverrideCategory | 'nicht_verfuegbar';

export interface CategorizedPeriod {
  from: string;
  to: string;
  category: PeriodCategory;
  source: PeriodSource;
}

export interface MonthlyBreakdownBucket {
  frei_buchbar: number;
  frei_nicht_buchbar: number;
  nicht_verfuegbar: number;
  booking: number;
  blocker: number;
  unbekannt: number;
}

export type MonthlyBreakdown = Record<string, Record<string, MonthlyBreakdownBucket>>;

export interface YearBreakdownEntry {
  total_days: number;
  by_category: Record<PeriodCategory, number>;
  /** Erkannte Buchungen im Jahr (maschinelle Transitions + manuelle booking-Overrides). */
  booking_signal_count: number;
  avg_booking_signal_nights: number | null;
}

export interface StayMetrics {
  year_breakdown: Record<string, YearBreakdownEntry>;
  current_year: string;
  /** Erkannte Buchungen über alle Jahre. */
  booking_signal_count_total: number;
  avg_booking_signal_nights_total: number | null;
  min_stay_platform: number | null;
  snapshot_count: number;
  history_days: number;
}

export interface UsageProfile {
  code: 'new' | 'long_term_blocked' | 'active_str' | 'sporadic' | 'low_demand' | 'mixed';
  label: string;
  rationale: string;
}

export interface Listing {
  id: number;
  search_id: number | null;
  city_id: number;
  state: string;
  registration_number: string | null;
  notes: string | null;
  responsible_person: string | null;
  title: string;
  address: string | null;
  scraped_address: string | null;
  scraped_city: string | null;
  scraped_zipcode: string | null;
  baugebiet: string | null;
  city: string | null;
  zipcode: string | null;
  latitude: number | null;
  longitude: number | null;
  address_verified: boolean;
  listing_type: string | null;
  platform: string;
  platform_listing_id: string | null;
  listing_url: string;
  size: string | null;
  guests_count: number | null;
  bedrooms: number | null;
  distance_to_center: string | null;
  neighborhood: string | null;
  reviews: string | null;
  rating: number | null;
  sponsored: string | null;
  host_name: string | null;
  host_type: string | null;
  host_since: string | null;
  price_per_night: number | null;
  currency: string;
  occupancy_rate: number | null;
  listing_created_date: string | null;
  google_street_view_url: string | null;
  resolved_url: string | null;
  resolved_platform: string | null;
  resolved_title: string | null;
  booked_dates: string | null;
  period_overrides?: string | null;
  categorized_periods?: CategorizedPeriod[] | null;
  monthly_breakdown?: MonthlyBreakdown | null;
  period_horizon?: string | null;
  calendar_raw?: string | null;
  availability?: string | null;
  stay_summary?: { min_nights: number | null; max_nights: number | null } | null;
  stay_metrics?: StayMetrics | null;
  usage_profile?: UsageProfile | null;
  marked_for_detail: boolean;
  detail_scrape_status: string | null;
  detail_scrape_error: string | null;
  detail_scraped_at: string | null;
  first_scraped_at: string;
  last_updated_at: string | null;
  description?: string;
  raw_data?: string;
  verfahren_id: number | null;
  vermieter_id: number | null;
  adresse_id: number | null;
  einheit_id: number | null;
  haus_id: number | null;
  is_online: boolean;
  offline_since: string | null;
  last_checked_at: string | null;
  detail_cron_schedule: string | null;
  external_url: string | null;
  offer_provider: string | null;
  offer_provider_id: string | null;
  zustaendig_user_id: number | null;
  zustaendig_context: string | null;
  zustaendig_assigned_at: string | null;
  assignment?: AssignmentInfo | null;
  deleted_at: string | null;
  deleted_by_user_id: number | null;
  deleted_reason: string | null;
  duplicate_of_id?: number | null;
  section_visibility?: InseratSectionVisibility;
}

export interface InseratSectionVisibility {
  registrierungsnummer: boolean;
  baugebiet: boolean;
}

export interface CitySectionVisibility {
  inserat: InseratSectionVisibility;
}

export const LISTING_STATES: Record<string, { label: string; color: string }> = {
  neu:                     { label: 'Neu',                    color: 'blue' },
  beobachtet:              { label: 'Beobachtet',             color: 'cyan' },
  verdachtsfall:           { label: 'Verdachtsfall',          color: 'yellow' },
  verfahren_eingeleitet:   { label: 'Verfahren eingeleitet',  color: 'purple' },
  kein_verstoss:           { label: 'Kein Verstoß',           color: 'green' },
};

export interface AuditLogEntry {
  id: number;
  action: string;
  field_name: string | null;
  old_value: string | null;
  new_value: string | null;
  comment: string | null;
  user_name: string | null;
  created_at: string;
}

export interface ListingStats {
  total: number;
  by_state: Record<string, number>;
  new_today: number;
  is_online_count: number;
}

export interface SearchCreateData {
  location: string;
  location_type?: string;
  max_listings?: number;
}

export interface DocumentItem {
  id: number;
  entity_type: string;
  entity_id: number;
  listing_id: number | null; // legacy, kept for backward compatibility
  city_id: number;
  document_type: string;
  template_name: string | null;
  filename: string;
  mime_type: string | null;
  file_size: number | null;
  version: number;
  notes: string | null;
  created_at: string;
  updated_at: string | null;
  linked_communication?: LinkedCommunicationSummary | null;
}

export interface DocumentTemplate {
  id: number;
  name: string;
  display_name: string;
  description: string | null;
}

export interface DocumentAnnotation {
  id: number;
  document_id: number;
  user_id: number | null;
  user_name: string | null;
  page_number: number | null;
  x_pct: number;
  y_pct: number;
  body: string;
  created_at: string;
  updated_at: string | null;
}

// --- Communications & Letterheads ---

export type CommunicationKind = 'call' | 'email' | 'letter';
export type CommunicationDirection = 'incoming' | 'outgoing';

export type CommunicationStatus = 'draft' | 'sent' | 'received';

export interface Communication {
  id: number;
  city_id: number;
  entity_type: string;
  entity_id: number;
  kind: CommunicationKind;
  direction: CommunicationDirection;
  status: CommunicationStatus | null;
  contact_name: string | null;
  contact_address: string | null;
  subject: string | null;
  body_text: string | null;
  occurred_at: string;
  finalized_at: string | null;
  document_id: number | null;
  document_filename: string | null;
  document_mime_type: string | null;
  letterhead_id: number | null;
  template_name: string | null;
  variables_json: string | null;
  created_by_user_id: number | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string | null;
}

// Prefill payload for the Letter Wizard.
export interface PropertyAddressCandidate {
  adresse_id: number;
  strasse: string | null;
  hausnummer: string | null;
  plz: string | null;
  ort: string | null;
  adresse_oneline: string;
  source: string;
}

export interface ListingPrefill {
  id: number;
  title: string | null;
  registration_number: string | null;
  platform: string | null;
  listing_url: string | null;
  host_name: string | null;
}

export interface CommunicationPrefillResponse {
  recipient_candidates: LetterRecipient[];
  property_address_candidates: PropertyAddressCandidate[];
  listing: ListingPrefill | null;
}

// Generic communication create / update bodies
export interface CommunicationCreateInput {
  kind: CommunicationKind;
  direction: CommunicationDirection;
  occurred_at?: string;
  contact_name?: string | null;
  contact_address?: string | null;
  subject?: string | null;
  body_text?: string | null;
  document_id?: number | null;
}

export interface CommunicationUpdateInput {
  occurred_at?: string;
  contact_name?: string | null;
  contact_address?: string | null;
  subject?: string | null;
  body_text?: string | null;
  document_id?: number | null;
}

export interface Letterhead {
  id: number;
  city_id: number;
  owner_user_id: number | null;
  owner_name: string | null;
  is_city_default: boolean;
  name: string;
  sender_block: string;
  contact_line: string | null;
  signature_block: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string | null;
}

export interface LetterheadInput {
  name: string;
  sender_block: string;
  contact_line: string | null;
  signature_block: string | null;
  is_active?: boolean;
  set_as_city_default?: boolean;
}

export type LetterTemplateVariableType = 'text' | 'number' | 'textarea' | 'checklist';

export interface LetterTemplateVariableDef {
  key: string;
  label: string;
  type: LetterTemplateVariableType;
  default: unknown;
  options?: string[];
}

export interface LetterTemplateDef {
  name: string;
  display_name: string;
  description: string | null;
  variables: LetterTemplateVariableDef[];
}

export interface LetterRecipient {
  anrede?: string | null;
  name: string;
  strasse: string;
  hausnummer: string;
  plz: string;
  ort: string;
  land: string;
}

export interface LinkedCommunicationSummary {
  id: number;
  kind: CommunicationKind;
  direction: CommunicationDirection;
  status: CommunicationStatus | null;
}

// --- Address hierarchy ---

export interface Adresse {
  id: number;
  strasse: string;
  hausnummer: string;
  plz: string | null;
  stadt: string | null;
  zustaendig_user_id?: number | null;
  zustaendig_context?: string | null;
  zustaendig_assigned_at?: string | null;
  assignment?: AssignmentInfo | null;
  deleted_at?: string | null;
  deleted_by_user_id?: number | null;
  deleted_reason?: string | null;
}

export interface Haus {
  id: number;
  typ: string | null;
  bezeichnung: string | null;
  baugenehmigung: string | null;
  einheiten_count: number;
}

export interface Einheit {
  id: number;
  bezeichnung: string;
  typ: string | null;
}

export interface EinheitHierarchy {
  einheit_id: number;
  einheit_bezeichnung: string;
  einheit_typ: string | null;
  haus_id: number;
  haus_bezeichnung: string;
  adresse_id: number;
  strasse: string;
  hausnummer: string;
  plz: string | null;
  stadt: string | null;
  other_verfahren: { id: number; aktenzeichen: string | null; staat: string }[];
}

export interface HierarchyResponse {
  adresse: Adresse;
  haus: { id: number; bezeichnung: string } | null;
  einheit: { id: number; bezeichnung: string; typ: string | null } | null;
}

// --- Verfahren (enforcement case) ---

export interface Verfahren {
  id: number;
  city_id: number;
  vermieter_id: number | null;
  einheit_id: number | null;
  aktenzeichen: string | null;
  staat: string;
  zustaendig: string | null;
  zustaendig_user_id: number | null;
  zustaendig_context: string | null;
  zustaendig_assigned_at: string | null;
  frist: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string | null;
  closed_at: string | null;
  inserate_count: number;
  deleted_at?: string | null;
  deleted_by_user_id?: number | null;
  deleted_reason?: string | null;
}

export interface VerfahrenDetail extends Verfahren {
  vermieter: VermieterSummary | null;
  inserate: InseratSummary[];
  einheit_hierarchy: EinheitHierarchy | null;
  assignment: AssignmentInfo | null;
}

export interface InseratSummary {
  id: number;
  title: string;
  platform: string;
  listing_url: string;
  state: string;
  scraped_address: string | null;
  scraped_city: string | null;
  host_name: string | null;
  host_type: string | null;
  first_scraped_at: string;
  vermieter_id: number | null;
  vermieter_name: string | null;
  adresse_id: number | null;
  adresse_str: string | null;
  haus_id: number | null;
  haus_bezeichnung: string | null;
  einheit_id: number | null;
  einheit_bezeichnung: string | null;
}

export interface VermieterSummary {
  id: number;
  name: string;
  typ: string | null;
}

export interface VerfahrenStats {
  total: number;
  by_state: Record<string, number>;
}

export interface StateInfo {
  key: string;
  label: string;
  color: string;
  recommended_transitions: string[];
}

export const VERFAHREN_STATES: Record<string, { label: string; color: string }> = {
  inserat_gefunden:          { label: 'Inserat gefunden',        color: 'blue' },
  ermittlung:                { label: 'Ermittlung',              color: 'cyan' },
  kein_verstoss:             { label: 'Kein Versto\u00df',             color: 'green' },
  anschreiben_versendet:     { label: 'Anschreiben versendet',    color: 'purple' },
  anhoerung:                 { label: 'Anh\u00f6rung',                  color: 'indigo' },
  stellungnahme_eingegangen: { label: 'Stellungnahme eingeg.',    color: 'teal' },
  verstoss_festgestellt:     { label: 'Versto\u00df festgestellt',      color: 'red' },
  untersagungsverfuegung:    { label: 'Untersagungsverf\u00fcgung',    color: 'red' },
  widerspruch:               { label: 'Widerspruch',              color: 'orange' },
  bussgeld:                  { label: 'Bu\u00dfgeld',                   color: 'red' },
  verfahren_abgeschlossen:   { label: 'Verfahren abgeschlossen',  color: 'green' },
};

export const VERFAHREN_STATE_ORDER = [
  'inserat_gefunden',
  'ermittlung',
  'anschreiben_versendet',
  'anhoerung',
  'stellungnahme_eingegangen',
  'verstoss_festgestellt',
  'untersagungsverfuegung',
  'widerspruch',
  'bussgeld',
  'kein_verstoss',
  'verfahren_abgeschlossen',
];

// --- Vermieter (host/landlord) ---

export interface Vermieter {
  id: number;
  city_id: number;
  anrede: string | null;
  name: string;
  typ: string | null;
  email: string | null;
  telefon: string | null;
  strasse: string | null;
  hausnummer: string | null;
  plz: string | null;
  ort: string | null;
  land: string | null;
  geburtsdatum: string | null;
  aktiv_seit: string | null;
  plattform_ids: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string | null;
  inserate_count: number;
  verfahren_count: number;
  deleted_at?: string | null;
  deleted_by_user_id?: number | null;
  deleted_reason?: string | null;
}

// --- Enriched list item types ---

export interface VerfahrenListItem extends Verfahren {
  vermieter_name: string | null;
  adresse_str: string | null;
  plz: string | null;
  zustaendig_user_name: string | null;
}

export interface ListingListItem extends Listing {
  vermieter_name: string | null;
  verfahren_aktenzeichen: string | null;
}

export interface AdresseListItem extends Adresse {
  latitude: number | null;
  longitude: number | null;
  haeuser_count: number;
  verfahren_count: number;
  created_at: string | null;
  deleted_at?: string | null;
  deleted_by_user_id?: number | null;
  deleted_reason?: string | null;
}

export interface HausListItem {
  id: number;
  typ: string | null;
  bezeichnung: string | null;
  baugenehmigung: string | null;
  adresse_id: number;
  adresse_str: string;
  plz: string | null;
  stadt: string | null;
  latitude: number | null;
  longitude: number | null;
  einheiten_count: number;
  verfahren_count: number;
  deleted_at?: string | null;
  deleted_by_user_id?: number | null;
  deleted_reason?: string | null;
}

export interface EinheitListItem {
  id: number;
  bezeichnung: string;
  typ: string | null;
  haus_id: number;
  haus_bezeichnung: string;
  adresse_id: number;
  adresse_str: string;
  plz: string | null;
  stadt: string | null;
  latitude: number | null;
  longitude: number | null;
  verfahren_count: number;
  inserate_count: number;
  deleted_at?: string | null;
  deleted_by_user_id?: number | null;
  deleted_reason?: string | null;
}

// --- Anfrage (inquiry/request) ---

export interface Anfrage {
  id: number;
  city_id: number;
  adresse_id: number | null;
  haus_id: number | null;
  einheit_id: number | null;
  verfahren_id: number | null;
  vermieter_id: number | null;
  adresse_text: string | null;
  zipcode: string | null;
  city: string | null;
  address_verified: boolean;
  status: string;
  notes: string | null;
  responsible_person: string | null;
  zustaendig_user_id: number | null;
  zustaendig_context: string | null;
  zustaendig_assigned_at: string | null;
  created_at: string;
  updated_at: string | null;
  deleted_at?: string | null;
  deleted_by_user_id?: number | null;
  deleted_reason?: string | null;
  assignment?: AssignmentInfo | null;
}

// --- Assignment / Aufgaben ---

export interface AssigneeInfo {
  id: number;
  full_name: string;
  email?: string | null;
}

export interface AssignmentInfo {
  user: AssigneeInfo | null;
  context: string | null;
  assigned_at: string | null;
  assigned_by: AssigneeInfo | null;
}

export interface AufgabeListItem {
  entity_type: 'verfahren' | 'anfrage' | 'inserat' | 'adresse';
  entity_id: number;
  title: string;
  subtitle: string | null;
  context: string | null;
  assigned_at: string | null;
  assigned_by: AssigneeInfo | null;
  assigned_to: AssigneeInfo | null;
}

export interface AufgabenResponse {
  items: AufgabeListItem[];
  total: number;
}

export interface CityMember {
  id: number;
  full_name: string;
  email: string;
  role: UserRole;
}

export const ANFRAGE_STATES: Record<string, { label: string; color: string }> = {
  anfrage_eingegangen:    { label: 'Anfrage eingegangen',    color: 'blue' },
  in_bearbeitung:         { label: 'In Bearbeitung',          color: 'cyan' },
  unterlagen_angefordert: { label: 'Unterlagen angefordert', color: 'yellow' },
  zulaessig:              { label: 'Zulässig',                color: 'green' },
  unzulaessig:            { label: 'Unzulässig',              color: 'red' },
  verfahren_eingeleitet:  { label: 'Verfahren eingeleitet',   color: 'purple' },
};
